import axios, { AxiosError } from "axios";
import { AuthTypeCheckInputType, AuthenticationInputType, PatientTEFCAAuthInfoDto, } from "healthex-common/auth.dtos";
import { err, voidResult } from "healthex-common/result";
import { apiConfig, authenticatedApiConfig, generateURL, retryTooManyRequests } from "./BaseApiService";
import { plainToInstance } from "class-transformer";
export class AuthService {
    constructor(environment) {
        this.environment = environment;
    }
    async generateTokenByPassword(email, password) {
        return await retryTooManyRequests(async () => {
            var _a, _b, _c;
            try {
                const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, { type: AuthenticationInputType.PASSWORD, email, password }, apiConfig());
                return response.data;
            }
            catch (e) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
                throw e;
            }
        });
    }
    async getAuthType(emailOrOrganizationId, redirectURL = window.location.href) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/type`, {
            type: "email" in emailOrOrganizationId ? AuthTypeCheckInputType.EMAIL : AuthTypeCheckInputType.ORGANIZATION,
            ...emailOrOrganizationId,
            redirectURL,
        }, apiConfig());
        return response.data;
    }
    async resetPassword(password, token) {
        var _a, _b, _c;
        try {
            await axios.put(`${this.environment.API_BASE_URL}/auth/reset_password`, { password }, authenticatedApiConfig({ token }));
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
            }
            throw e;
        }
    }
    async changePassword(input) {
        var _a, _b, _c;
        try {
            await axios.put(`${this.environment.API_BASE_URL}/auth/change_password`, input, apiConfig());
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
            }
            throw e;
        }
    }
    async getSmartAuthUrl(organizationId, redirectUrl, launch, userType) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/organizations/${organizationId}/smart-auth-url`, {
            ...apiConfig(),
            params: {
                redirectUrl,
                launch,
                userType,
            },
        });
        return response.data;
    }
    async getShortLivedTokenForAppLaunch(authenticationProvider, token) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token/short-lived`, { token }, authenticatedApiConfig(authenticationProvider));
        return response.data;
    }
    async generateTokenFromShortLivedToken(token) {
        return await retryTooManyRequests(async () => {
            var _a, _b, _c;
            try {
                const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, { type: AuthenticationInputType.EXISTING_TOKEN, token }, apiConfig());
                return response.data;
            }
            catch (e) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
                throw e;
            }
        });
    }
    async generateTokenBySmartCode(smartCode, userType, organizationId, redirectUri, sandboxMode) {
        return await retryTooManyRequests(async () => {
            var _a, _b, _c;
            try {
                const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, { type: AuthenticationInputType.SMART, smartCode, userType, organizationId, redirectUri, sandboxMode }, apiConfig());
                return response.data;
            }
            catch (e) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
                throw e;
            }
        });
    }
    async generateTokenByPatientId(authenticationProvider, patientId, organizationName) {
        var _a, _b, _c;
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/auth/patientbyid/token`, { patientId, organizationName }, authenticatedApiConfig(authenticationProvider));
            return response.data;
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async generateTokenByPatientIdWithIAL2ForTesting(authenticationProvider, patientId, organizationId) {
        var _a, _b, _c;
        try {
            const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token/testing`, { patientId, organizationId, type: AuthenticationInputType.CLEAR_IAL2 }, authenticatedApiConfig(authenticationProvider));
            return response.data;
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async generateTokenByExistingToken(token) {
        return await retryTooManyRequests(async () => {
            var _a, _b, _c;
            try {
                const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, { type: AuthenticationInputType.EXISTING_TOKEN, token }, apiConfig());
                return response.data;
            }
            catch (e) {
                if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                    return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
                }
                throw e;
            }
        });
    }
    async generateTokenByClearGFIDWithIAL2(authCode, redirectUri, organizationId, checkAll, useFallback, sandboxMode, heapGlobalEventProperties, linkingToken, existingAuthToken) {
        var _a, _b, _c;
        try {
            const body = {
                type: AuthenticationInputType.CLEAR_IAL2,
                authCode,
                redirectUri,
                organizationId,
                checkAll,
                useFallback,
                sandboxMode,
                heapGlobalEventProperties,
                linkingToken,
                existingAuthToken,
            };
            const response = await axios.post(`${this.environment.API_BASE_URL}/auth/token`, body, apiConfig());
            return response.data;
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async linkAccount(linkingToken, authenticationProvider) {
        const body = { linkingToken };
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/link-account`, body, authenticatedApiConfig(authenticationProvider));
        return response.data;
    }
    async sendPasswordResetEmail(email) {
        const response = await axios.post(`${this.environment.API_BASE_URL}/auth/forgot_password`, { email }, apiConfig());
        return response.status;
    }
    async getAuthenticatedUserWithDetails(authenticationProvider) {
        const response = await axios.get(`${this.environment.API_BASE_URL}/auth/user-details`, authenticatedApiConfig(authenticationProvider));
        return response.data;
    }
    async getPatientTEFCAAuthInfo(patientId, authenticationProvider) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/tefca-auth-info", { patientId }), authenticatedApiConfig(authenticationProvider));
        return plainToInstance(PatientTEFCAAuthInfoDto, response.data);
    }
}
